<div class="page">
  @if (isLoading()) {
    <app-branded-loader [label]="'common.loading' | translate" />
  } @else if (dog(); as currentDog) {
  <div class="mb-6">
    <button type="button" appBackNavigation class="back-link mb-5">
      <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
      </svg>
      {{ 'dogProfile.back' | translate }}
    </button>
    <div class="card flex flex-col gap-6 p-6 sm:flex-row sm:items-center md:p-8">
      <img [src]="currentDog.imageUrl || defaultDogImage" [alt]="currentDog.name"
        class="w-24 h-24 md:w-32 md:h-32 shrink-0 rounded-3xl object-cover border-4 border-white dark:border-slate-800 shadow-md">
      <div class="min-w-0">
        <h1 class="profile-title text-slate-900 dark:text-white">{{ currentDog.name }}</h1>
        <p class="text-lg text-slate-600 dark:text-slate-300 mt-1">
          {{ currentDog.breed | breedName }}@if (currentDog.age) {, {{ currentDog.age }} {{ 'humanProfile.years' | translate }}}
        </p>
        @if (owner(); as dogOwner) {
        <p class="text-md text-slate-500 dark:text-slate-400 mt-2">
          {{ 'dogProfile.owner' | translate }}:
          <a [routerLink]="['/human-profile', dogOwner.id]"
            class="link-accent">
            {{ dogOwner.name }}
          </a>
        </p>
        }
      </div>
    </div>
  </div>

  <!-- Stats Section -->
  <div class="my-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
    <div class="card flex flex-col items-center justify-center p-6 text-center">
      <p class="eyebrow">{{ 'dogProfile.stats.racesParticipated' | translate }}</p>
      <p class="mt-2 text-3xl font-black text-slate-900 dark:text-white">{{ dogStats().numberOfRaces }}</p>
    </div>
    <div class="card flex flex-col items-center justify-center p-6 text-center">
      <p class="eyebrow">{{ 'dogProfile.stats.totalDistance' | translate }}</p>
      <p class="mt-2 text-3xl font-black text-slate-900 dark:text-white">{{ dogStats().totalDistanceCovered | distance }} <span class="text-base">km</span></p>
      @if (distanceBreakdown().length) {
        <div class="mt-3 flex w-full flex-wrap justify-center gap-x-4 gap-y-1 border-t border-slate-100 pt-3 dark:border-slate-800">
          @for (item of distanceBreakdown(); track item.label) {
            <div class="flex items-center gap-1.5">
              <span class="text-lg">{{ item.label }}</span>
              <span class="text-sm font-black text-slate-700 dark:text-slate-300">{{ item.value | distance }}</span>
            </div>
          }
        </div>
      }
    </div>
    <div class="card flex flex-col items-center justify-center p-6 text-center">
      <p class="eyebrow">{{ 'dogProfile.stats.averageSpeed' | translate }}</p>
      <p class="mt-2 text-3xl font-black text-slate-900 dark:text-white">{{ formatSpeed(dogStats().averageSpeed) }} <span class="text-base">km/h</span></p>
    </div>
  </div>

  <!-- Palmarès Section -->
  @if (hasPodiums()) {
  <div class="mb-8">
    <h2 class="section-title mb-6 flex items-center gap-2">
      <span class="p-1.5 bg-yellow-400 rounded-lg text-yellow-900">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      </span>
      {{ 'dogProfile.palmares.title' | translate }}
    </h2>
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-6">
      <!-- Or -->
      <div class="card p-6 border-b-4 border-b-yellow-400 flex items-center space-x-4">
        <div class="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center text-yellow-900 shadow-inner">
          <span class="text-2xl">🥇</span>
        </div>
        <div>
          <p class="text-3xl font-black text-slate-900 dark:text-slate-100">{{ podiums().first }}</p>
          <p class="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{{ 'dogProfile.palmares.victories' | translate }}</p>
        </div>
      </div>
      <!-- Argent -->
      <div class="card p-6 border-b-4 border-b-slate-300 flex items-center space-x-4">
        <div class="w-12 h-12 bg-slate-300 rounded-full flex items-center justify-center text-slate-700 shadow-inner">
          <span class="text-2xl">🥈</span>
        </div>
        <div>
          <p class="text-3xl font-black text-slate-900 dark:text-slate-100">{{ podiums().second }}</p>
          <p class="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{{ 'dogProfile.palmares.silver' | translate }}</p>
        </div>
      </div>
      <!-- Bronze -->
      <div class="card p-6 border-b-4 border-b-amber-600 flex items-center space-x-4">
        <div class="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center text-amber-100 shadow-inner">
          <span class="text-2xl">🥉</span>
        </div>
        <div>
          <p class="text-3xl font-black text-slate-900 dark:text-slate-100">{{ podiums().third }}</p>
          <p class="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{{ 'dogProfile.palmares.bronze' | translate }}</p>
        </div>
      </div>
      <!-- Chocolat -->
      <div class="card p-6 border-b-4 border-b-orange-900 flex items-center space-x-4">
        <div class="w-12 h-12 bg-orange-900 rounded-full flex items-center justify-center text-orange-100 shadow-inner">
          <span class="text-2xl">🍫</span>
        </div>
        <div>
          <p class="text-3xl font-black text-slate-900 dark:text-slate-100">{{ podiums().fourth }}</p>
          <p class="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{{ 'dogProfile.palmares.chocolate' | translate }}</p>
        </div>
      </div>
    </div>
  </div>
  }

  <!-- Race History -->
  <h2 class="section-title mb-6">{{ 'dogProfile.raceHistory' | translate }}</h2>
  
  <div class="space-y-8">
    @for (group of groupedResults(); track group.event.id) {
    <div class="card overflow-hidden">
      <!-- Event Header -->
      <div class="bg-slate-50 dark:bg-slate-700/50 p-6 border-b border-slate-200 dark:border-slate-700 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h4 class="text-xl font-black text-slate-900 dark:text-slate-100">{{ group.event.name }}</h4>
          <div class="flex items-center gap-4 mt-1 text-sm text-slate-500 dark:text-slate-400 font-medium">
            <span class="flex items-center gap-1">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {{ formatDate(group.event.date) }}
            </span>
            <span class="flex items-center gap-1">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {{ group.event.location | displayValue }}
            </span>
          </div>
        </div>
        <div class="hidden md:block">
           <span class="accent-soft px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest">
             {{ 'common.racesCount' | translate:group.results.length }}
           </span>
        </div>
      </div>

      <!-- Races within event -->
      <div class="divide-y divide-slate-100 dark:divide-slate-700">
        @for (result of group.results; track result.id) {
        <div class="p-6 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
          <div class="grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
            <!-- Discipline & Name -->
            <div class="md:col-span-1">
              <span class="inline-block px-2 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">
                {{ result.race.discipline | disciplineLabel:'common.course' }}
              </span>
              <h5 class="text-sm font-bold text-slate-900 dark:text-slate-100">{{ result.race.name }}</h5>
            </div>

            <!-- Human Partner -->
            <div class="md:col-span-1">
              <p class="text-xs text-slate-400 uppercase font-black tracking-widest mb-1">{{ 'dogProfile.table.humanPartner' | translate }}</p>
              @if (result.team?.human?.id && result.team?.human?.name) {
              <a [routerLink]="['/human-profile', result.team!.human.id]" class="link-accent text-sm">
                {{ result.team?.human?.name }} <app-registered-star [humanId]="result.team?.human?.id" />
              </a>
              } @else {
                <span class="text-sm text-slate-400">{{ null | displayValue }}</span>
              }
            </div>

            <div class="md:col-span-1">
               <div class="flex flex-col gap-1">
                 <div class="flex items-baseline gap-1">
                   <span class="text-[9px] font-black text-slate-400 uppercase tracking-widest mr-0.5">{{ 'common.overall' | translate }}</span>
                   <span class="text-sm font-black text-slate-900 dark:text-slate-100">
                      @if (result.position) { #{{ result.position }} } @else { {{ null | displayValue }} }
                   </span>
                   <span class="text-[10px] text-slate-400 font-bold">/{{ participantCounts()[result.raceId] || 0 }}</span>
                 </div>
                 @if (result.category) {
                   <div class="flex items-center gap-1 px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 w-fit">
                     <span class="text-[8px] font-black text-accent uppercase tracking-tighter mr-0.5">
                       {{ 'common.category' | translate }} {{ result.category }}
                     </span>
                     <span class="text-xs font-black text-slate-900 dark:text-slate-100">
                        @if (result.categoryRank) { #{{ result.categoryRank }} } @else { {{ null | displayValue }} }
                     </span>
                     <span class="text-[10px] text-slate-400 font-bold">
                       /{{ categoryParticipantCounts()[result.id] || 0 }}
                     </span>
                   </div>
                 }
               </div>
            </div>

            <!-- Time & Speed -->
            <div class="md:col-span-1 text-right">
              <p class="text-sm font-mono font-black text-slate-900 dark:text-slate-100">{{ result.statusCode ? (result.statusCode | resultStatus:result.statusLabel:result.statusRaw) : (result.statusLabel || formatTime(result.timeSeconds)) }}</p>
              @if (result.timeSeconds > 0) {
                <p class="text-xs text-slate-500">{{ formatSpeed(result.averageSpeedKmph || (result.race.distanceKm / (result.timeSeconds/3600))) }} km/h</p>
              }
            </div>
          </div>
        </div>
        }
      </div>
    </div>
    } @empty {
    <div class="rounded-3xl p-12 text-center border-2 border-dashed border-slate-200 dark:border-slate-800">
      <div class="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg class="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 002-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      </div>
      <p class="text-slate-500 dark:text-slate-400 font-bold">{{ 'dogProfile.noResults' | translate }}</p>
    </div>
    }
  </div>
  }
</div>
